import React, { FC, useState } from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Paper,
    Button,
    Typography,
    CircularProgress,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    Divider,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { Stack } from '@mui/system';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import { darkBlue, darkBlue2, white } from '@styles/colors';
import { Company } from '@staffedupusa/shared-components';
import RockerboxListingRow from '@features/rockerboxDashboard/components/rockerboxListingRow';
import { useGetRockerboxApplicants } from '../hooks/useGetRockerboxApplicants';
import RockerboxApplicantRow from './RockerboxApplicantRow';
import { Nullable } from 'vitest';
import RockerboxDcuments from './RockerboxDcuments';

type RockerboxApplicantsProps = {
    companyId: number;
    wotc_enabled: boolean | string | null;
};

const RockerboxApplicants: FC<RockerboxApplicantsProps> = ({ companyId, wotc_enabled }) => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchValue, setSearchValue] = useState('');
    const [columnNo, setColumnNo] = useState(0);
    const [sortedDirection, setSortedDirection] = useState('asc');
    const [applicationsListAlertText, setApplicationsListAlertText] = useState<string>('');
    const [statusValue, setStatusValue] = useState('applicants');

    const { rockerboxList, isLoadingrockerboxList } = useGetRockerboxApplicants({
        companyId,
        searchValue,
        page,
        columnNo,
        sortedDirection,
        page_length: rowsPerPage,
        enabled: wotc_enabled,
        status: statusValue,
        setAlertText: setApplicationsListAlertText,
    });

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleSearch = () => {
        setPage(1);
    };

    const handleSort = (column: number) => {
        const isAsc = columnNo === column && sortedDirection === 'asc';
        setSortedDirection(isAsc ? 'desc' : 'asc');
        setColumnNo(column);
    };

    const renderSortArrow = (column: number) => {
        if (columnNo === column) {
            return sortedDirection === 'asc' ? (
                <Stack direction='row' >
                    <NorthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
                    <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                </Stack>
            ) : (
                <Stack direction='row'>
                    <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                    <SouthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
                </Stack>
            );
        }
        return (
            <Stack direction='row'>
                <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
            </Stack>
        );
    };

    return (
        <div>
            <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>

                <Stack spacing={4} sx={{ borderRadius: 5 }} direction={{ xs: 'column', sm: 'row' }} justifyContent={{ sm: 'center' }} alignItems="center">
                    <ToggleButtonGroup
                        value={statusValue}
                        exclusive
                        onChange={(event, newValue) => {
                            setPage(1);
                            setStatusValue(newValue);
                        }}
                    >
                        <ToggleButton
                            disabled={statusValue === 'applicants'}
                            value="applicants"
                            sx={{
                                padding: "5px 30px",
                                borderBottomLeftRadius: '5px',
                                borderTopLeftRadius: '5px',
                                backgroundColor: statusValue === 'applicants' ? darkBlue2 : 'white',
                                color: statusValue === 'applicants' ? 'white' : 'default',
                                '&.Mui-selected': {
                                    backgroundColor: darkBlue2,
                                    color: 'white',
                                }
                            }}
                        >
                            Applicants
                        </ToggleButton>
                        <Divider
                            orientation="vertical"
                        />
                        <ToggleButton
                            disabled={statusValue === 'documents'}
                            value='documents'
                            sx={{
                                padding: "5px 30px",
                                borderBottomRightRadius: '5px',
                                borderTopRightRadius: '5px',
                                backgroundColor: statusValue === 'documents' ? darkBlue2 : 'white',
                                color: statusValue === 'documents' ? 'white' : 'default',
                                '&.Mui-selected': {
                                    backgroundColor: darkBlue2,
                                    color: 'white',
                                }
                            }}
                        >
                            Documents
                        </ToggleButton>
                        <Divider
                            orientation="vertical"
                        />
                        <ToggleButton
                            disabled={statusValue === 'billing'}
                            value='billing'
                            sx={{
                                padding: "5px 30px",
                                borderBottomRightRadius: '5px',
                                borderTopRightRadius: '5px',
                                backgroundColor: statusValue === 'billing' ? darkBlue2 : 'white',
                                color: statusValue === 'billing' ? 'white' : 'default',
                                '&.Mui-selected': {
                                    backgroundColor: darkBlue2,
                                    color: 'white',
                                }
                            }}
                        >
                            Billing
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>
                {statusValue === 'applicants' ? (
                    <>
                        <Paper component="form" sx={{ pl: '10px', display: 'flex', alignItems: 'center', maxWidth: 400, width: '100%', borderRadius: 5 }}>
                            <InputBase
                                sx={{ ml: 1, flex: 1 }}
                                placeholder="Search"
                                inputProps={{ 'aria-label': 'search' }}
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                            <IconButton type="button" sx={{ px: '10px' }} aria-label="search" onClick={handleSearch}>
                                <SearchIcon />
                            </IconButton>
                        </Paper>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell onClick={() => handleSort(0)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Wotc Id</b> {renderSortArrow(0)}
                                            </Stack>
                                        </TableCell>
                                        <TableCell onClick={() => handleSort(1)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Candidate Name</b> {renderSortArrow(1)}
                                            </Stack>

                                        </TableCell>
                                        <TableCell onClick={() => handleSort(2)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Hired Date</b> {renderSortArrow(2)}
                                            </Stack>

                                        </TableCell>
                                        <TableCell onClick={() => handleSort(3)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Payrate</b> {renderSortArrow(3)}
                                            </Stack>

                                        </TableCell>
                                        <TableCell onClick={() => handleSort(4)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Hours Weekend</b> {renderSortArrow(4)}
                                            </Stack>

                                        </TableCell>
                                        <TableCell onClick={() => handleSort(5)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Credit Earned</b> {renderSortArrow(5)}
                                            </Stack>

                                        </TableCell>
                                        <TableCell onClick={() => handleSort(6)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Forms</b> {renderSortArrow(6)}
                                            </Stack>

                                        </TableCell>
                                        <TableCell onClick={() => handleSort(7)}>
                                            <Stack direction='row' justifyContent='space-between'>
                                                <b>Delete</b> {renderSortArrow(7)}
                                            </Stack>

                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoadingrockerboxList ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Box sx={{
                                                    display: 'flex', alignItems: "center", justifyContent: "center",
                                                    textAlign: "center", alignSelf: "center",
                                                    width: 'auto', height: '150px', margin: "auto"
                                                }}>
                                                    <CircularProgress />
                                                </Box>
                                            </TableCell>
                                        </TableRow>

                                    ) : rockerboxList?.object?.applicants?.length > 0 ? (
                                        <>
                                            {rockerboxList.object.applicants.map(
                                                (row: {
                                                    email: String;
                                                    hired: Date;
                                                    pay_rate: String | null;
                                                    hours_worked: String | null;
                                                    wotc_credit: String | null;
                                                    applicant: {
                                                        id: number;
                                                        s_name: String;
                                                    };
                                                }) => (
                                                    <RockerboxApplicantRow row={row} />
                                                ),
                                            )}
                                        </>
                                    ) : rockerboxList?.object?.applicants?.length < 1 ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Box sx={{
                                                    display: 'flex', alignItems: "center", justifyContent: "center",
                                                    textAlign: "center", alignSelf: "center",
                                                    width: 'auto', height: '150px', margin: "auto"
                                                }}>

                                                    No Data Found
                                                </Box>
                                            </TableCell>
                                        </TableRow>

                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Box sx={{
                                                    display: 'flex', alignItems: "center", justifyContent: "center",
                                                    textAlign: "center", alignSelf: "center",
                                                    width: 'auto', height: '300px', margin: "auto"
                                                }}>

                                                    {applicationsListAlertText}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={rockerboxList?.object?.total_application || 0}
                            rowsPerPage={rowsPerPage}
                            page={page - 1}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                    </>
                ) : statusValue === 'documents' ? (
                    <>
                        <RockerboxDcuments companyId={companyId} statusValue={statusValue} />
                    </>
                ) : statusValue === 'billing' && (
                    <>
                        Billing
                    </>
                )}
            </Stack>
        </div >
    );
}

export default RockerboxApplicants;
